import cartesian from "fast-cartesian";
import { useFormikContext } from "formik";
import { isEqual } from "lodash";
import { Price } from "utils";

import {
  Combination,
  ProductFormValues,
  VariantOption,
  Variants,
  Inventory,
  VariantWithOptions,
} from "../types";

export const COMBINATION_COUNT_LIMIT = 100;

// Wraps Formik's `useFormikContext` hook to add some additional functionality.
export default function useProductFormFormik() {
  const formik = useFormikContext<ProductFormValues>();

  const setCombinationInventory = (
    combination: Combination,
    inventory: Inventory
  ) => {
    const index = formik.values.combinations.findIndex((c) =>
      isEqual(c, combination)
    );
    formik.setFieldValue(`combinations[${index}].inventory`, inventory);
  };

  const setCombinations = () => {
    const combinations = generateCombinations(
      formik.values.variants,
      formik.values.combinations
    );
    formik.setFieldValue("combinations", combinations);
  };

  const saveVariant = (
    variantWithOptions: VariantWithOptions,
    savedVariantName?: string
  ) => {
    const { variantsWithOptions } = formik.values.variants;

    // find the saved variant that matches the already saved variant name, or append at end
    const idx = savedVariantName
      ? variantsWithOptions
          .map((vo) => vo.variant.name)
          .indexOf(savedVariantName)
      : variantsWithOptions.length;

    if (!isEqual(variantsWithOptions[idx], variantWithOptions)) {
      const key = `variants.variantsWithOptions[${idx}]`;
      formik.setFieldValue(key, variantWithOptions);

      // iterate over all combinations to update variant names
      const renamedCombinations = renameCombinations(
        formik.values.combinations,
        variantWithOptions.variant.name,
        savedVariantName
      );

      formik.setFieldValue("combinations", renamedCombinations);
    }
  };

  const removeVariant = (variantName?: string) => {
    const filtered = formik.values.variants.variantsWithOptions.filter(
      (v) => v.variant.name !== variantName
    );
    formik.setFieldValue("variants.variantsWithOptions", filtered);
  };

  const isEditing = !!formik.initialValues.uuid;

  const combinationCountExceedsLimit =
    formik.values.combinations.length > COMBINATION_COUNT_LIMIT;

  function savePrice(price: Price, index?: number) {
    const idx = index ?? formik.values.prices.length;

    if (!isEqual(formik.values.prices[idx], price)) {
      formik.setFieldValue(`prices[${idx}]`, price);
    }
  }

  function removePrice(index = 0) {
    const filtered = formik.values.prices.filter((p, i) => i !== index);
    formik.setFieldValue("prices", filtered);
  }

  return {
    ...formik,
    isEditing,
    combinationCountExceedsLimit,
    setCombinations,
    setCombinationInventory,
    saveVariant,
    removeVariant,
    savePrice,
    removePrice,
  };
}

// Generate all possible combinations given a list of variants and their options.
const generateCombinations = (
  variants: Variants,
  existingCombinations: Combination[]
): Combination[] => {
  const opts: VariantOption[][] = variants.variantsWithOptions.map(
    ({ variant, options }) =>
      options.map((option) => ({ variant: variant.name, option: option.name }))
  );

  const newCombinations = cartesian(opts).map((variantOptions) => ({
    variantOptions,
    inventory: { enabled: false, quantity: 0 },
  }));

  return mergeCombinations(newCombinations, existingCombinations);
};

// merge old combinations into new ones to preserve inventories
const mergeCombinations = (
  newCombinations: Combination[],
  existingCombinations: Combination[]
) => {
  // cache the existing combinations into a kv map
  const cache = new Map(
    existingCombinations.map((c) => [combinationKey(c), c])
  );

  // iterate over new combinations checking cache or returning new generated value
  return newCombinations.map((c) => cache.get(combinationKey(c)) || c);
};

// generate a deterministic key for indexing
const combinationKey = (combination: Combination) => {
  return JSON.stringify(
    combination.variantOptions.sort((a, b) =>
      a.variant.localeCompare(b.variant)
    )
  );
};

const renameCombinations = (
  combinations: Combination[],
  newVariantName: string,
  savedVariantName?: string
) => {
  // we're creating a new variant, so nothing to rename in combinations
  if (!savedVariantName) return combinations;

  return combinations.map((c) => {
    const variantOptions = c.variantOptions.map((vo) => {
      // update variant name to new one if it matches saved name, or keep as is
      const variant =
        vo.variant === savedVariantName ? newVariantName : vo.variant;

      return { ...vo, variant };
    });

    return { ...c, variantOptions };
  });
};
